import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useApi } from "@/core/api";
import { businessKey } from "./query";

export interface GoogleBusinessAccount {
  name: string;
  accountName: string;
  type: "PERSONAL" | "BUSINESS";
  verificationState: "UNVERIFIED" | "VERIFIED";
  vettedState: "NOT_VETTED" | "VETTED";
}

export interface GoogleBusinessLocation {
  name: string;
  storeCode: string;
  title: string;
}

export interface GoogleBusinessAccountLocation {
  id: string;
  account: string;
  location: string;
  title: string;
  created_at: string;
  updated_at: string;
}

export const useGetGoogleBusinessAccounts = (businessId?: string | null) => {
  const api = useApi();

  return useQuery({
    queryKey: ["google-business-accounts", businessId],
    queryFn: (): Promise<GoogleBusinessAccount[]> =>
      api
        .get(`/google/business_accounts?business_id=${businessId}`)
        .then((response) => response.data),
    enabled: !!businessId,
  });
};

export const useGetGoogleBusinessLocations = (
  businessId?: string | null,
  accountName?: string | null,
) => {
  const api = useApi();

  return useQuery({
    queryKey: ["google-business-locations", businessId, accountName],
    queryFn: (): Promise<GoogleBusinessLocation[]> =>
      api
        .get(
          `/google/business_locations?business_id=${businessId}&account=${accountName}`,
        )
        .then((response) => response.data),
    enabled: !!businessId && !!accountName,
  });
};

export const useGetGoogleBusinessAccountLocations = (
  businessId?: string | null,
) => {
  const api = useApi();

  return useQuery({
    queryKey: ["google-business-account-locations", businessId],
    queryFn: (): Promise<GoogleBusinessAccountLocation[]> =>
      api
        .get(`/businesses/${businessId}/google_business_account_locations`)
        .then((response) => response.data),
    retry: (failureCount, error) => {
      if (error instanceof AxiosError && error.response?.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
    enabled: !!businessId,
  });
};

export const useCreateGoogleBusinessAccountLocation = (
  businessId?: string | null,
) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      account: string;
      location: string;
      title: string;
    }) =>
      api
        .post(`/businesses/${businessId}/google_business_account_locations`, {
          account: data.account,
          location: data.location,
          title: data.title,
        })
        .then((response) => response.data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["businesses"] });
      void queryClient.invalidateQueries({ queryKey: businessKey(businessId) });
      void queryClient.invalidateQueries({
        queryKey: ["google-business-account-locations", businessId],
      });
    },
  });
};

export const useDeleteGoogleBusinessAccountLocation = (
  businessId?: string | null,
) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (googleBusinessAccountLocationId: string) =>
      api
        .delete(
          `/businesses/${businessId}/google_business_account_locations/${googleBusinessAccountLocationId}`,
        )
        .then((response) => response.data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["businesses"] });
      void queryClient.invalidateQueries({ queryKey: businessKey(businessId) });
      void queryClient.invalidateQueries({
        queryKey: ["google-business-account-locations", businessId],
      });
    },
  });
};
